import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PIN_OUTCOMES } from "@/lib/canvassingUtils";
import { getCanvassingStats } from "@/lib/canvassingUtils";
import { MapPin, Target, TrendingUp } from "lucide-react";
import type { CanvassingPin, PinOutcome } from "@/hooks/useCanvassing";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface CanvassingPinListProps {
  pins: CanvassingPin[];
  onUpdatePin: (pinId: string, outcome: PinOutcome, notes?: string) => Promise<void>;
}

export function CanvassingPinList({ pins, onUpdatePin }: CanvassingPinListProps) {
  const stats = getCanvassingStats(pins);

  return (
    <div className="flex flex-col h-full">
      {/* Stats header */}
      <div className="p-4 border-b border-border bg-muted/30 space-y-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <Target className="w-4 h-4 text-primary" /> Session Stats
        </h3>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-foreground">{stats.knocked}</p>
            <p className="text-xs text-muted-foreground">Knocked</p>
          </div>
          <div>
            <p className="text-lg font-bold text-green-600 dark:text-green-400">{stats.appointments}</p>
            <p className="text-xs text-muted-foreground">Appts</p>
          </div>
          <div>
            <p className="text-lg font-bold text-primary">{stats.conversionRate}%</p>
            <p className="text-xs text-muted-foreground">Conv.</p>
          </div>
        </div>
      </div>

      {/* Pin list */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {pins.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No pins yet</p>
              <p className="text-xs">Click the map to add a door</p>
            </div>
          ) : (
            pins.slice(0, 50).map(pin => {
              const config = PIN_OUTCOMES[pin.outcome as PinOutcome];
              return (
                <div
                  key={pin.id}
                  className="p-2.5 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {pin.homeowner_name || pin.address || "Unknown Door"}
                      </p>
                      {pin.address && pin.homeowner_name && (
                        <p className="text-xs text-muted-foreground truncate">{pin.address}</p>
                      )}
                    </div>
                    <Badge className={cn("text-xs shrink-0", config?.color)}>
                      {config?.emoji} {config?.label}
                    </Badge>
                  </div>
                  {pin.notes && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{pin.notes}</p>
                  )}
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    {format(new Date(pin.knocked_at), "h:mm a")}
                  </p>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
